import React from 'react';
import { Link } from 'react-router-dom';
import { BellRingIcon, CheckIcon, LockIcon, TriangleAlertIcon } from 'lucide-react';
import type { ChoirEvent, Member } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';
import { useApp } from '../../contexts/AppContext';
import { missingFields } from '../../utils/personal';
import { formatRange, fullName, personalFieldLabel } from '../../utils/labels';
interface TravelDataPanelProps {
  event: ChoirEvent;
  udelezenci: Member[];
}
export function TravelDataPanel({
  event,
  udelezenci
}: TravelDataPanelProps) {
  const {
    canViewPrivate,
    pushToast
  } = useApp();
  const potovanje = event.potovanje;
  if (!potovanje) return null;
  const vrstice = udelezenci.map(m => ({
    m,
    manjka: missingFields(m, potovanje.obveznaPolja)
  }));
  const popolni = vrstice.filter(v => v.manjka.length === 0).length;
  const nepopolni = vrstice.length - popolni;
  return <div className="space-y-4" data-id="element-199" data-mp="10f76ae3ntpi7">
      <Card className="p-5" data-id="element-200" data-mp="10e5lvj0r7qnt">
        <h2 className="text-base font-semibold text-ink" data-id="element-201" data-mp="15dijmyt5yi56">Potovalni podatki</h2>
        <dl className="mt-3 grid gap-3 border-t border-line pt-3 sm:grid-cols-2" data-id="element-202" data-mp="15be5lo1jw2r6">
          {[['Trajanje', formatRange(event.datum, event.datumDo)], ['Odhod', potovanje.odhod ?? '—'], ['Vrnitev', potovanje.prihod ?? '—'], ['Zborno mesto', potovanje.zborno ?? '—'], ['Nastanitev', potovanje.nastanitev?.ime ?? '—'], ['Št. udeležencev', String(udelezenci.length)]].map(([k, v]) => <div key={k} data-id="element-203" data-mp="qfge4j2knmla">
              <dt className="text-[13px] text-ink-muted" data-id="element-204" data-mp="qiccg8ic0da9">{k}</dt>
              <dd className="text-sm font-medium text-ink" data-id="element-205" data-mp="vgdrmicmzsgg">{v}</dd>
            </div>)}
        </dl>
        {potovanje.opis && <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-sm leading-relaxed text-ink" data-id="element-206" data-mp="21xhfxeqaefux">
            {potovanje.opis}
          </p>}
      </Card>

      <Card className="p-5" data-id="element-207" data-mp="6r6x1mg2e9g3">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-208" data-mp="6qf6wr58l3x7">
          <div data-id="element-209" data-mp="2pv7ti13rq8f">
            <h2 className="text-base font-semibold text-ink" data-id="element-210" data-mp="2qddt6dkhitj">Popolnost osebnih podatkov</h2>
            <p className="mt-0.5 text-sm text-ink-muted" data-id="element-211" data-mp="gpw9gzp6z1oy">
              Za to gostovanje potrebujemo:{' '}
              {potovanje.obveznaPolja.map(p => personalFieldLabel[p].toLowerCase()).join(', ')}.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => pushToast('Opomnik za dopolnitev podatkov je bil poslan.')} data-id="element-212" data-mp="3f81uuni093nb">
            <BellRingIcon className="h-4 w-4" data-id="element-213" data-mp="3ty94qety5ece" />
            Pošlji opomnik
          </Button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-214" data-mp="3p44u9llh2o6k">
          <div className="rounded-lg bg-canvas px-3 py-2.5" data-id="element-215" data-mp="31flmd76c4df0">
            <p className="text-xl font-semibold text-green-600" data-id="element-216" data-mp="2whk60u0ams78">{popolni}</p>
            <p className="text-[12px] text-ink-muted" data-id="element-217" data-mp="2wfg0k7mqxl4u">Podatki popolni</p>
          </div>
          <div className="rounded-lg bg-canvas px-3 py-2.5" data-id="element-218" data-mp="1by6vythh6zpm">
            <p className="text-xl font-semibold text-amber-600" data-id="element-219" data-mp="1gqhxg0rv6tka">{nepopolni}</p>
            <p className="text-[12px] text-ink-muted" data-id="element-220" data-mp="12z37tb3gbu49">Potrebna dopolnitev</p>
          </div>
        </div>

        {!canViewPrivate && <p className="mt-4 flex items-start gap-2 rounded-lg bg-canvas px-3 py-2.5 text-xs leading-relaxed text-ink-muted" data-id="element-221" data-mp="17183efdgn0tj">
            <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-222" data-mp="1unwmv5i4b1c9" />
            Brez dovoljenja „Dostop do osebnih podatkov“ vidite le, ali so podatki popolni — ne pa
            vsebine. Dovoljenje dodeli skrbnik v Nastavitvah.
          </p>}

        <ul className="mt-4 divide-y divide-line border-t border-line" data-id="element-223" data-mp="1qnmhnv25geyg">
          {vrstice.map(({
          m,
          manjka
        }) => <li key={m.id} className="flex flex-wrap items-center gap-3 py-2.5" data-id="element-224" data-mp="a9cw18pal4bb">
              <Avatar member={m} size="sm" data-id="element-225" data-mp="f9ious7aswuz" />
              <span className="min-w-0 flex-1" data-id="element-226" data-mp="f75xxpjkbc5s">
                {canViewPrivate ? <Link to={`/admin/clani/${m.id}`} className="block truncate text-sm font-medium text-ink hover:text-green-700" data-id="element-227" data-mp="uk6zas9n2i2x">
                    {fullName(m)}
                  </Link> : <span className="block truncate text-sm font-medium text-ink" data-id="element-228" data-mp="gsioqtf1g1o2">{fullName(m)}</span>}
                {canViewPrivate && manjka.length > 0 && <span className="block text-[12px] text-amber-700" data-id="element-229" data-mp="18i5bps4eg4fs">
                    Manjka: {manjka.map(p => personalFieldLabel[p].toLowerCase()).join(', ')}
                  </span>}
              </span>
              {manjka.length === 0 ? <Badge className="bg-green-50 text-green-700 border-green-200" data-id="element-230" data-mp="zkbhcmq73kv9">
                  <CheckIcon className="h-3 w-3" data-id="element-231" data-mp="3p8lvmwybtmbc" />
                  Podatki popolni
                </Badge> : <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-id="element-232" data-mp="1zq5i3gf6fvnv">
                  <TriangleAlertIcon className="h-3 w-3" data-id="element-233" data-mp="1zo2iq5s4wq6h" />
                  {canViewPrivate ? 'Manjkajo podatki' : 'Potrebna dopolnitev'}
                </Badge>}
            </li>)}
        </ul>
      </Card>
    </div>;
}